import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { toast } from "sonner";
import { Plus, Trash2, Terminal } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function CustomCommandsPanel() {
  const { active } = useServer();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ trigger: "", response: "", description: "", enabled: true });

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/custom-commands/${active.id}`).then((r) => setList(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const create = async (e) => {
    e.preventDefault();
    if (!form.trigger.trim() || !form.response.trim()) return;
    await api.post(`/features/custom-commands/${active.id}`, form);
    toast.success("Command created");
    setForm({ trigger: "", response: "", description: "", enabled: true });
    refresh();
  };

  const remove = async (id) => {
    await api.delete(`/features/custom-commands/${active.id}/${id}`);
    refresh();
  };

  return (
    <div data-testid="custom-commands-panel" className="space-y-8">
      <div>
        <div className="overline mb-2"><Terminal className="inline w-4 h-4 mr-2 -mt-1" />SERVER MANAGEMENT</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Custom Commands</h1>
        <p className="text-zinc-400 mt-2">Define text triggers (e.g. <code className="text-brand-orange">!rules</code>) that the bot responds to.</p>
      </div>

      <form onSubmit={create} className="tactical-card p-6 space-y-4" data-testid="cc-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Trigger</label>
            <input className="tactical-input" placeholder="!rules" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} data-testid="cc-trigger" />
          </div>
          <div>
            <label className="overline block mb-2">Description</label>
            <input className="tactical-input" placeholder="Show server rules" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cc-desc" />
          </div>
        </div>
        <div>
          <label className="overline block mb-2">Response</label>
          <textarea className="tactical-input min-h-[80px]" placeholder="Response text the bot will send..." value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} data-testid="cc-response" />
        </div>
        <div className="flex justify-end">
          <button type="submit" className="btn-primary" data-testid="cc-create-btn"><Plus className="w-4 h-4" /> Create</button>
        </div>
      </form>

      <div className="tactical-card p-6">
        <h2 className="font-heading text-xl font-bold uppercase mb-4">All Commands ({list.length})</h2>
        <div className="space-y-2">
          {list.length === 0 && <div className="text-zinc-500 font-mono-tabular text-sm py-6 text-center">No custom commands yet</div>}
          {list.map((c) => (
            <div key={c.id} className="border border-brand-line p-3 rounded-lg flex items-center gap-3" data-testid={`cc-row-${c.id}`}>
              <code className="text-brand-orange font-mono-tabular text-sm shrink-0">{c.trigger}</code>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{c.response}</div>
                <div className="text-xs text-zinc-500 truncate">{c.description}</div>
              </div>
              <button onClick={() => remove(c.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
